import { useCallback, useEffect, useRef, useState } from "react";
import { BookOpen, Map, ShieldCheck, Users as UsersIcon } from "lucide-react";
import { AdminLayout, Button } from "cfw-lib-admin-ui/preact";
import "cfw-lib-admin-ui/styles.css";
import type { AdminIdentity, AdminMap, MapSummary } from "../shared/types";
import { api, errorText } from "./api";
import { MapEditor } from "./MapEditor";
import { MapLibrary } from "./MapLibrary";
import { Documentation } from "./Documentation";
import { Users } from "./Users";
import { Loading, Notice, PageTitle } from "./ui";
import "./admin.css";
import "./workflows.css";
import "./shell.css";

type Route =
  | { section: "maps"; id?: string }
  | { section: "users" }
  | { section: "documentation"; mapId?: string };
function readRoute(): Route {
  const hash = window.location.hash.slice(1);
  if (hash === "/users") return { section: "users" };
  if (hash.split("?")[0] === "/documentation") {
    const mapId =
      new URLSearchParams(hash.split("?")[1]).get("map") || undefined;
    return { section: "documentation", mapId };
  }
  if (hash.startsWith("/maps/")) {
    try {
      return { section: "maps", id: decodeURIComponent(hash.slice(6)) };
    } catch {
      return { section: "maps" };
    }
  }
  return { section: "maps" };
}
function routeHash(route: Route) {
  if (route.section === "documentation")
    return `#/documentation${route.mapId ? `?map=${encodeURIComponent(route.mapId)}` : ""}`;
  return route.section !== "maps"
    ? `#/${route.section}`
    : route.id
      ? `#/maps/${encodeURIComponent(route.id)}`
      : "#/maps";
}

export function App() {
  const [identity, setIdentity] = useState<AdminIdentity | null>(null);
  const [maps, setMaps] = useState<MapSummary[]>([]);
  const [route, setRoute] = useState<Route>(readRoute);
  const [selected, setSelected] = useState<AdminMap | null>(null);
  const [loading, setLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [menu, setMenu] = useState(false);
  const dirty = useRef(false);
  const currentRoute = useRef(route);
  const heading = useRef<HTMLElement>(null);
  const loadSequence = useRef(0);
  const canEdit = identity?.role === "owner" || identity?.role === "editor";
  const onDirtyChange = useCallback(
    (value: boolean) => {
      const active = currentRoute.current;
      // A completed request can outlive its editor after browser navigation.
      if (active.section === "maps" && active.id === selected?.id)
        dirty.current = value;
    },
    [selected?.id],
  );
  async function load() {
    setLoading(true);
    setError("");
    try {
      const user = await api.me();
      setIdentity(user);
      setMaps(await api.maps());
    } catch (error) {
      setError(errorText(error));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, []);
  useEffect(() => {
    function beforeUnload(event: BeforeUnloadEvent) {
      if (dirty.current) {
        event.preventDefault();
        event.returnValue = "";
      }
    }
    function hashChange() {
      const next = readRoute();
      if (routeHash(next) === routeHash(currentRoute.current)) return;
      if (
        dirty.current &&
        !window.confirm("Discard your unsaved map changes?")
      ) {
        window.history.replaceState(null, "", routeHash(currentRoute.current));
        return;
      }
      dirty.current = false;
      currentRoute.current = next;
      setRoute(next);
      setMenu(false);
      setError("");
      if (next.section !== "maps" || next.id) setNotice("");
      heading.current?.focus({ preventScroll: true });
      window.scrollTo(0, 0);
    }
    window.addEventListener("beforeunload", beforeUnload);
    window.addEventListener("hashchange", hashChange);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      window.removeEventListener("hashchange", hashChange);
    };
  }, []);
  useEffect(() => {
    const sequence = ++loadSequence.current;
    setSelected(null);
    if (!identity || route.section !== "maps" || !route.id) {
      setMapLoading(false);
      return;
    }
    setMapLoading(true);
    setError("");
    api
      .map(route.id)
      .then((map) => {
        if (loadSequence.current === sequence) setSelected(map);
      })
      .catch((error) => {
        if (loadSequence.current === sequence) setError(errorText(error));
      })
      .finally(() => {
        if (loadSequence.current === sequence) setMapLoading(false);
      });
  }, [route, identity]);
  function navigate(next: Route) {
    window.location.hash = routeHash(next);
  }
  function updated(map: AdminMap) {
    setMaps((previous) =>
      [map, ...previous.filter((item) => item.id !== map.id)].sort((a, b) =>
        b.updatedAt.localeCompare(a.updatedAt),
      ),
    );
  }
  function deleted(map: AdminMap) {
    setMaps((previous) => previous.filter((item) => item.id !== map.id));
    const active = currentRoute.current;
    if (active.section !== "maps" || active.id !== map.id) return;
    dirty.current = false;
    loadSequence.current++;
    setSelected(null);
    setMapLoading(false);
    setError("");
    setNotice(`“${map.title}” was deleted.`);
    navigate({ section: "maps" });
  }
  function created(map: AdminMap) {
    updated(map);
    navigate({ section: "maps", id: map.id });
  }
  return (
    <AdminLayout
      menuOpen={menu}
      onMenuOpenChange={setMenu}
      mainRef={heading}
      mainId="main-content"
      navigationId="workspace-navigation"
      navigationLabel="Workspace navigation"
      classNames={{
        root: "em-admin-shell",
        header: "em-topbar",
        headerTitle: "em-topbar-title",
        title: "em-page-title",
        headerActions: "em-topbar-actions",
        sidebar: "em-sidebar",
        mainNavigation: "em-nav-group em-nav-group-main",
        adminNavigation: "em-nav-group em-nav-group-admin",
        identity: "em-sidebar-user",
        body: "em-admin-body",
        main: "em-main",
        footer: "em-admin-footer",
        menuToggle: "em-menu-button",
        skipLink: "em-skip-link",
      }}
      titleFallback={
        loading ||
        (identity &&
          route.section === "maps" &&
          route.id &&
          (mapLoading || !selected))
          ? route.section === "users"
            ? "Users"
            : route.section === "documentation"
              ? "Documentation"
              : route.id
                ? "Map editor"
                : "Map library"
          : undefined
      }
      headerActions={
        identity?.local ? (
          <span className="em-local-badge">Local preview</span>
        ) : undefined
      }
      mainNavigation={
        <>
          <a
            className="em-product"
            href="#/maps"
            aria-label="Gem home"
            title="Gem"
          >
            <img
              className="em-product-icon"
              src="/brand/gem.svg"
              width="30"
              height="30"
              alt=""
              aria-hidden="true"
            />
            <span className="em-product-name">Gem</span>
          </a>
          <a
            href="#/maps"
            className={route.section === "maps" ? "active" : ""}
            aria-current={route.section === "maps" ? "page" : undefined}
          >
            <Map size={18} /> <span>Maps</span>
            <small>{maps.length}</small>
          </a>
          <a
            href="#/documentation"
            className={route.section === "documentation" ? "active" : ""}
            aria-current={
              route.section === "documentation" ? "page" : undefined
            }
          >
            <BookOpen size={18} /> <span>Documentation</span>
          </a>
        </>
      }
      adminNavigation={
        identity?.role === "owner" && (
          <a
            href="#/users"
            className={route.section === "users" ? "active" : ""}
            aria-current={route.section === "users" ? "page" : undefined}
          >
            <UsersIcon size={18} /> <span>Users</span>
          </a>
        )
      }
      identity={{
        name: identity?.name || undefined,
        roleLabel: identity
          ? identity.role[0].toUpperCase() + identity.role.slice(1)
          : "Cloudflare Access",
        email: identity?.email || "Protected admin workspace",
        detail: identity
          ? identity.local
            ? "Local development"
            : "Cloudflare Access"
          : undefined,
        icon: <ShieldCheck size={17} />,
      }}
      footer={<span>GSMA Gem</span>}
    >
      {loading ? (
        <Loading label="Opening your workspace…" />
      ) : !identity ? (
        <div className="em-auth-error">
          <PageTitle>Let’s get you connected</PageTitle>
          <Notice>{error || "Sign in to open Gem."}</Notice>
          <Button onClick={() => void load()}>Try again</Button>
        </div>
      ) : (
        <>
          {error && <Notice>{error}</Notice>}
          {notice && route.section === "maps" && !route.id && (
            <Notice success>{notice}</Notice>
          )}
          {route.section === "documentation" ? (
            <Documentation
              key={route.mapId || "documentation"}
              maps={maps}
              initialMapId={route.mapId}
            />
          ) : route.section === "users" ? (
            identity.role === "owner" ? (
              <Users
                identity={identity}
                onIdentityChange={(next) => {
                  setIdentity(next);
                  if (!next)
                    setError("Your access to this workspace has been removed.");
                  else if (next.role !== "owner") navigate({ section: "maps" });
                }}
              />
            ) : (
              <div className="em-empty em-panel">
                <ShieldCheck size={36} />
                <PageTitle>Owner access required</PageTitle>
                <p>Your workspace owner can manage people and permissions.</p>
                <button
                  className="em-button"
                  onClick={() => navigate({ section: "maps" })}
                >
                  Back to maps
                </button>
              </div>
            )
          ) : route.id ? (
            mapLoading ? (
              <Loading label="Opening map…" />
            ) : selected ? (
              <MapEditor
                key={selected.id}
                initial={selected}
                canEdit={canEdit}
                canDelete={identity.role === "owner"}
                onDeleted={deleted}
                onBack={() => navigate({ section: "maps" })}
                onChange={updated}
                onDirtyChange={onDirtyChange}
              />
            ) : (
              <button
                className="em-button em-button-secondary"
                onClick={() => navigate({ section: "maps" })}
              >
                Back to maps
              </button>
            )
          ) : (
            <MapLibrary
              maps={maps}
              canEdit={canEdit}
              openMap={(id) => navigate({ section: "maps", id })}
              onCreated={created}
            />
          )}
        </>
      )}
    </AdminLayout>
  );
}
